import { useRef, useEffect, useState, ReactNode, Children, default as React } from 'react'
import GoogleMapsWrapper from './GoogleMapsWrapper'

type Position = {
  latitude: number,
  longitude: number
}

type MapProps = {
  center: Position,
  zoom: number,
  width: number,
  height: number,
  children: ReactNode
}

const Map = ({ center, zoom, width, height, children }: MapProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map>()

  useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {
        center: { lat: center.latitude, lng: center.longitude },
        zoom,
        mapId: 'd3c69086e102031b'
      }))
    }
  }, [ref, map])

  useEffect(() => {
    if (map) {
      map.setCenter({ lat: center.latitude, lng: center.longitude })
    }
  }, [center])

  return <div ref={ref} style={{ width, height }}>
    {Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        const params: Partial<MarkerProps> = { map }
        return React.cloneElement(child, params);
      }
    })}
  </div>
}

type MarkerProps = {
  position: Position,
  map?: google.maps.Map,
  label?: string
  onMarkerMoved?: (marker: Marker) => void
}
const Marker = (props: MarkerProps) => {
  const { position } = props
  const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement>();

  useEffect(() => {
    if (!marker && props.map) {
      const newMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: position.latitude, lng: position.longitude },
        map: props.map,
        content: props.label ? new google.maps.marker.PinElement({
          glyph: props.label,
        }).element : null,
        gmpDraggable: props.onMarkerMoved !== undefined
      })
      setMarker(newMarker);

      if (props.onMarkerMoved) {
        newMarker.addListener('dragend', () => {
          const position = newMarker.position as {lat: number, lng: number}
          props.onMarkerMoved!({
            label: props.label,
            position: {
              latitude: position.lat, longitude: position.lng
            }
          })
        });
      }
    }

    return () => {
      if (marker) {
        marker.map = null // remove marker from map on unmount
      }
    };
  }, [marker, props.map]);

  return null;
};

export type Marker = {
  position: Position
  label?: string
}

export type Props = {
  apiKey: string
  center: Position
  zoom: number
  width: number
  height: number
  markers: Marker[],
  onMarkerMoved?: (marker: Marker) => void
}

const MapComponent = ({ apiKey, center, zoom, width, height, markers, onMarkerMoved }: Props) => {
  return <GoogleMapsWrapper apiKey={apiKey}>
    <Map center={center} zoom={zoom} width={width} height={height}>
      {markers.map((x, i) => <Marker position={x.position} label={x.label} key={`${x.position.latitude} ${x.position.longitude} ${i}`} onMarkerMoved={onMarkerMoved} />)}
    </Map>
  </GoogleMapsWrapper>
}

export default MapComponent
